import * as DialogPrimitive from 'radix-ui/dialog';
import { type KeyboardEvent, type ReactNode, type RefObject, useRef, useState } from 'react';
import { Button } from '../Button/Button';
import { cx } from '../cx';
import { MOTION, useMotion } from '../motion';
import { nestedLayerAttributes } from '../nestedLayer';
import { OpenWord } from '../OpenWord/OpenWord';
import { useIsDesktop } from '../useIsDesktop';
import { useSwipeDismiss } from './useSwipeDismiss';

/**
 * APPLE'S SHEET, IN TWO SHAPES (wave 3, S-C2 and research SHELL-RULES section 7).
 *
 *  - THE ATTACHED SHEET, from the desktop breakpoint up: "In macOS, a sheet is a cardlike
 *    view with rounded corners that floats on top of its parent window. The parent window is
 *    dimmed while the sheet is onscreen" (hig:
 *    https://developer.apple.com/design/human-interface-guidelines/sheets). It hangs from the
 *    top of the window rather than sitting in the middle of it, because that is what
 *    "attached" means, and it carries NO close X: "in macOS and tvOS apps, people expect to
 *    find a button in the main content view" (hig:
 *    https://developer.apple.com/design/human-interface-guidelines/modality) - the corner
 *    close button is the iOS and watchOS pattern, and the kit's only Close Button masters
 *    are iOS ones.
 *  - THE PHONE SHEET, below it: the kit's `Sheets/{Light,Dark}/iPhone/Large Detent` with the
 *    grabber. Single-view sheets carry Cancel leading and confirmation trailing in the
 *    top bar (hig: https://developer.apple.com/design/human-interface-guidelines/sheets).
 *
 * WHERE THE BUTTONS GO. Cancel on the leading side, the default button on the trailing side,
 * in the phone top bar or at the bottom trailing edge on desktop: "Always place the default button on the
 * trailing side of a row ... Cancel buttons are typically on the leading side of a row"
 * (hig: https://developer.apple.com/design/human-interface-guidelines/alerts), and AppKit
 * gives the first button Return and any button titled Cancel Escape (doc:
 * https://developer.apple.com/documentation/appkit/nsalert/addbutton(withtitle:)). `confirm`
 * builds that pair, so a caller does not hand-roll it and Return really activates it - unless
 * the default is DESTRUCTIVE, where the HIG asks for Cancel to be the default instead and this
 * binds Return to nothing.
 *
 * A DIRTY CLOSE ASKS. `dirty` turns Escape and a click on the dimmed window into the
 * discard question rather than a silent loss; the sheet's own Cancel is the answer to the
 * same question and therefore asks too.
 *
 * `useIsDesktop` places one copy of the actions in the correct bar. Alerts opt in to
 * their existing bottom button layout; a destructive form is still a sheet.
 */
export type DialogVariant = 'auto' | 'center' | 'sheet';

/**
 * HOW FAR UP THE SCREEN A PHONE SHEET COMES. `large` is the sheet this product has always
 * drawn and stays the default; `medium` is the kit's Medium Detent, for a sheet whose content
 * is one short question and whose answer is read against the screen behind it.
 * Meaningless on the desktop, where the sheet is attached to the window instead.
 */
export type DialogDetent = 'large' | 'medium';
export type DialogWidth = 'sm' | 'md' | 'lg';

/**
 * The sheet's default button, and the Cancel that HIG Sheets requires beside it: "If you
 * provide a Done button, always pair it with a Cancel button ... Relying solely on the Done
 * button implies that completing the task is the only way to exit."
 */
export interface DialogConfirm {
  /** A verb naming what confirming does - "Create the role", never "OK". */
  readonly label: string;
  readonly onConfirm: () => void;
  readonly disabled?: boolean | undefined;
  readonly loading?: boolean | undefined;
  /** `danger` for a destructive default; never the primary role on a destructive action. */
  readonly tone?: 'primary' | 'danger' | undefined;
}

export interface DialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Called after the exit settles, so a conditional caller can unmount its form. */
  readonly onExited?: (() => void) | undefined;
  /** Always required: it is the accessible name of the dialog. */
  readonly title: string;
  readonly description?: string | undefined;
  /**
   * The OPEN id of a description nobody has decided yet (DESIGN.md D4, Part 3 item 6): it is
   * drawn dotted under it (`OpenWord`).
   */
  readonly descriptionOpenWord?: string | undefined;
  /** Hides the title visually (still announced) for dense sheets. */
  readonly hideTitle?: boolean | undefined;
  readonly variant?: DialogVariant | undefined;
  /** The phone sheet's height. `large` (the default) is the full sheet; `medium` is the kit's Medium Detent. */
  readonly detent?: DialogDetent | undefined;
  readonly width?: DialogWidth | undefined;
  /** Alert questions retain their bottom buttons. Forms use the default sheet layout. */
  readonly alert?: boolean | undefined;
  /**
   * The bottom row, when a caller builds it itself. `confirm` is the construction to prefer:
   * it puts Cancel and the default button where Apple puts them and binds Return.
   */
  readonly footer?: ReactNode | undefined;
  /** Cancel and confirmation in the phone top bar or desktop footer, with Return on the latter. */
  readonly confirm?: DialogConfirm | undefined;
  /** The leading button's title. "Cancel" is the one AppKit gives Escape. */
  readonly cancelLabel?: string | undefined;
  /**
   * THE DISMISS BUTTON A SHEET WITH NO OTHER WAY OUT NEEDS. The attached sheet has no close
   * X, so a caller that passes neither `footer` nor `confirm` gets one plain button at the
   * bottom trailing edge - which is the "button in the main content view" HIG Modality asks
   * for. `false` opts out; so does `hideTitle`, because a surface with no visible title is a
   * palette rather than the titled sheet that rule is about (the command menu).
   */
  readonly dismissLabel?: string | false | undefined;
  /**
   * THERE ARE UNSAVED EDITS IN HERE. Escape, a click on the dimmed window and Cancel all ask
   * before they throw them away.
   */
  readonly dirty?: boolean | undefined;
  /**
   * WHERE FOCUS GOES WHEN THE OPENER DID NOT SURVIVE THE DIALOG (added 2026-08-22).
   *
   * The rule below returns focus to the element that had it and skips a DETACHED one,
   * because focusing a node that has left the document is how you end up with no focus at
   * all. That leaves one case with nowhere to go, and it is not exotic: a confirmation whose
   * whole purpose is to delete the row its own opener sits in. The caller names a focusable
   * that outlives the deletion - the list's own "Add files", a section's heading - and it is
   * used ONLY as a fallback, so a cancelled confirmation still returns to the control that
   * asked.
   */
  readonly returnFocusTo?: RefObject<HTMLElement | null> | undefined;
  /**
   * THIS DIALOG OPENS FROM INSIDE ANOTHER DISMISSABLE LAYER - a `Popover`, a `CellEditor` -
   * and interactions inside it must not read as "outside" that layer (`ui/nestedLayer.ts`).
   *
   * Opt-in, because the right answer is not the same everywhere: a dialog opened from a
   * dropdown MENU should close that menu, and a confirmation opened from a cell editor must
   * not close the editor and unmount itself with it. Harmless when there is no enclosing
   * layer - nothing asks.
   */
  readonly nested?: boolean | undefined;
  /** Local portal host, so a preview inherits its own motion tokens. */
  readonly portalContainer?: HTMLElement | undefined;
  readonly children: ReactNode;
}

const WIDTHS: Record<DialogWidth, string> = {
  sm: 'md:max-w-sm',
  md: 'md:max-w-lg',
  lg: 'md:max-w-3xl',
};

const SHEET_SHAPE = 'fixed inset-x-0 bottom-0 max-h-[92dvh] rounded-t-sheet border-t border-line';

/**
 * THE MEDIUM DETENT (wave 5 round 4, B4). The phone sheet above is the kit's LARGE detent -
 * full width, attached to the bottom edge, up to the top of the screen - and it is the wrong
 * shape for a sheet that asks one short question while the list behind it is the context for
 * the answer. The kit ships the other one:
 * kit: Apple iOS 27 UI Kit.sketch / Sheets/Light/iPhone/Medium Detent, whose Stack group sits
 * at (8,415) 386x451 inside the 402x874 screen - so the card is inset 8 on the leading,
 * trailing and bottom edges (402 - 386 - 8 = 8, 874 - 415 - 451 = 8), is rounded on all four
 * corners because it touches no edge, and stands 451 of the 874 the screen is tall.
 * The numbers are `--sheet-medium-*` in tokens.css.
 * hig: https://developer.apple.com/design/human-interface-guidelines/sheets - "In iOS,
 * iPadOS, and visionOS, a sheet is a modal view that partially covers the underlying content".
 */
const MEDIUM_SHAPE =
  'fixed inset-x-(--sheet-medium-inset) bottom-(--sheet-medium-inset) ' +
  'h-(--sheet-medium-height) max-h-[92dvh] rounded-sheet border border-line';

/**
 * ATTACHED, WHICH MEANS IT HANGS FROM THE TOP OF THE WINDOW. A macOS sheet slides out from
 * under the title bar and stays there; a card parked in the middle of the window is a web
 * modal. The offset from the top edge and the height ceiling are the product's own space
 * scale (`product:` - Apple describes the sheet as floating over its parent window and
 * publishes no offset for a window it does not own), and together they leave the same gap
 * above and below so a full-height sheet never runs off the window.
 */
const ATTACHED_SHAPE =
  'fixed top-8 left-1/2 w-[calc(100%-2*var(--gutter))] max-h-[calc(100dvh-4rem)] ' +
  '-translate-x-1/2 rounded-sheet border border-line';

/** `auto` = the phone sheet up to the desktop breakpoint, the attached sheet from there on. */
const AUTO_SHAPE = cx(
  SHEET_SHAPE,
  'md:inset-x-auto md:bottom-auto md:top-8 md:left-1/2 md:w-[calc(100%-2*var(--gutter))]',
  'md:max-h-[calc(100dvh-4rem)] md:-translate-x-1/2 md:rounded-sheet md:border',
);

const SHAPES: Record<DialogVariant, string> = {
  auto: AUTO_SHAPE,
  sheet: SHEET_SHAPE,
  center: ATTACHED_SHAPE,
};

/**
 * The animation belongs to the SHAPE, not to the component: a sheet slides from
 * the edge it is attached to, an attached sheet arrives in place, and `auto` switches
 * between the two at the same breakpoint its shape does. The keyframes and the
 * timing live in ui/motion.css; this only says which surface this is. Wave 3 changed no
 * keyframe: the attached sheet keeps the arrival the centred dialog had.
 */
const ANIMATIONS: Record<DialogVariant, string> = {
  auto: 'chrome-anim-auto',
  sheet: MOTION.rise,
  center: MOTION.grow,
};

/** Return activates the default button, unless the focused thing is using Return itself. */
function returnIsTheDefaultButton(event: KeyboardEvent<HTMLElement>): boolean {
  if (event.key !== 'Enter') return false;
  if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return false;
  const target = event.target;
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLTextAreaElement) return false;
  if (target instanceof HTMLButtonElement || target instanceof HTMLAnchorElement) return false;
  return target.closest('[role="listbox"], [role="menu"], [role="grid"], [role="tree"]') === null;
}

export function Dialog({
  open,
  onOpenChange,
  onExited,
  title,
  description,
  descriptionOpenWord,
  hideTitle = false,
  variant = 'auto',
  detent = 'large',
  width = 'md',
  alert = false,
  footer,
  confirm,
  cancelLabel = 'Cancel',
  dismissLabel = 'Close',
  dirty = false,
  returnFocusTo,
  nested = false,
  children,
  portalContainer,
}: DialogProps) {
  const isDesktop = useIsDesktop();
  const motion = useMotion(open, { onExited });
  const attached = variant === 'center' || (variant === 'auto' && isDesktop);
  const phoneBar = !isDesktop && !alert && (!hideTitle || footer === undefined);
  const [asking, setAsking] = useState(false);

  /**
   * EVERY WAY OUT GOES THROUGH ONE DOOR: Escape, the dimmed window, the swipe and Cancel. That is the only way `dirty` can be honoured without each caller
   * remembering to ask.
   */
  const requestClose = (next: boolean): void => {
    if (!next && dirty) {
      setAsking(true);
      return;
    }
    onOpenChange(next);
  };
  const swipe = useSwipeDismiss(() => requestClose(false));

  /**
   * WHERE FOCUS CAME FROM, and why this is ours to remember.
   *
   * `@radix-ui/react-dialog`'s modal content handles `onCloseAutoFocus` by calling
   * `event.preventDefault()` and then focusing its own `Trigger` (verified in the
   * installed 1.1.23 source). This Dialog is CONTROLLED - the caller owns `open`, and the
   * button that opens a sheet sits in a toolbar row rather than wrapped in a
   * `Dialog.Trigger` - so that ref is always null: nothing gets focused, and the
   * `preventDefault` also suppresses FocusScope's own restore. Every dialog in the
   * product therefore dropped focus on <body> when it closed, which costs a keyboard or
   * screen-reader user their place in the page on every Escape.
   *
   * Captured DURING THE RENDER that opens the dialog, deliberately: an effect would run
   * after FocusScope's mount autofocus has already moved focus into the sheet, so it
   * would remember the close button. This render happens before the commit, so
   * `activeElement` is still whatever the user clicked.
   */
  const opener = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  if (open && !wasOpen.current) {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  wasOpen.current = open;

  /** Both portalled elements carry it: the overlay is as much "inside this dialog" as the
   * panel is, and a press on it is a press on this dialog's own dismiss affordance. */
  const layerMark = nestedLayerAttributes(nested);

  /**
   * THE BOTTOM ROW, in the order Apple's alert buttons are laid out. `footer` is the caller's
   * own row and is left exactly as it was; `confirm` is the pair; and a sheet with neither
   * still needs the one button HIG Modality asks for, on the shape that has no corner X.
   */
  const actions: ReactNode =
    footer !== undefined ? (
      footer
    ) : confirm !== undefined ? (
      <div
        className={
          phoneBar
            ? 'flex items-center justify-between gap-2'
            : 'flex flex-col-reverse gap-2 md:flex-row md:justify-end'
        }
      >
        <Button
          variant="secondary"
          className={phoneBar ? 'shrink-0' : 'w-full md:w-auto'}
          onClick={() => requestClose(false)}
        >
          {cancelLabel}
        </Button>
        <Button
          variant={confirm.tone === 'danger' ? 'danger' : 'primary'}
          className={phoneBar ? 'shrink-0' : 'w-full md:w-auto'}
          disabled={confirm.disabled === true}
          loading={confirm.loading === true}
          onClick={confirm.onConfirm}
        >
          {confirm.label}
        </Button>
      </div>
    ) : phoneBar ? (
      <div className="flex items-center justify-between gap-2">
        <Button variant="secondary" onClick={() => requestClose(false)}>
          {cancelLabel}
        </Button>
        {dismissLabel !== false && dismissLabel !== 'Close' && dismissLabel !== cancelLabel && (
          <Button variant="primary" onClick={() => requestClose(false)}>
            {dismissLabel}
          </Button>
        )}
      </div>
    ) : attached && dismissLabel !== false && !hideTitle ? (
      <div className="flex justify-end">
        <Button variant="secondary" onClick={() => requestClose(false)}>
          {dismissLabel}
        </Button>
      </div>
    ) : null;

  const bottomRow = phoneBar ? null : actions;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={requestClose}>
      <DialogPrimitive.Portal container={portalContainer}>
        <DialogPrimitive.Overlay
          className={cx(MOTION.fade, 'fixed inset-0 bg-overlay layer-overlay')}
          {...layerMark}
        />
        <DialogPrimitive.Content
          ref={motion.ref}
          {...layerMark}
          data-dialog-kind={alert ? 'alert' : 'sheet'}
          className={cx(
            'chrome-raised flex flex-col text-ink shadow-sheet layer-overlay md:shadow-popover',
            /* The detent only ever changes the PHONE sheet: `center` is the attached desktop
               sheet and `auto` is already the desktop sheet from `md:` up, so a medium detent
               below that breakpoint cannot reach either. */
            detent === 'medium' && !attached ? MEDIUM_SHAPE : SHAPES[variant],
            ANIMATIONS[attached ? 'center' : 'sheet'],
            WIDTHS[width],
          )}
          /*
           * WHILE THE FINGER HOLDS THE SHEET, THE ENTRANCE HAS TO LET GO OF IT. A CSS
           * animation outranks an inline style, and the Rise family fills `both`
           * (`ui/motion.css`, `.motion-rise[data-state="open"]`), so the entrance's own
           * final transform went on winning and the sheet stood still under the finger:
           * measured on the fixture build at 375 as inline `translateY(150px)` against a
           * computed `matrix(1, 0, 0, 1, 0, 0)` with the animation already `finished`.
           * The dismiss itself always worked, which is why the gesture read as broken
           * rather than as absent. The offset returns to zero on release in the same
           * commit that closes the sheet, so the exit keeps its animation.
           */
          style={
            swipe.offset > 0
              ? { transform: `translateY(${swipe.offset}px)`, animation: 'none' }
              : undefined
          }
          onKeyDown={(event) => {
            /*
              RETURN NEVER CONFIRMS A DESTRUCTIVE SHEET. AppKit gives the first button the
              Return key, and the HIG's own rule for a destructive one is the other way
              round: "Consider making Cancel the default button when the destructive action
              isn't the most likely choice"
              (https://developer.apple.com/design/human-interface-guidelines/alerts). A sheet
              that deletes a webhook on a stray Return is exactly the loss the confirmation
              exists to prevent, so the key is bound only to a `primary` default.
            */
            if (confirm === undefined || confirm.tone === 'danger') return;
            if (confirm.disabled === true || confirm.loading === true) return;
            if (!returnIsTheDefaultButton(event)) return;
            event.preventDefault();
            confirm.onConfirm();
          }}
          onCloseAutoFocus={(event) => {
            /**
             * Radix composes the caller's handler FIRST and skips its own once the event
             * is prevented, so this replaces the null-Trigger focus rather than racing
             * it. `isConnected` matters: a dialog opened from a menu item has an opener
             * that no longer exists by the time it closes, and focusing a detached node
             * is how you end up with no focus at all.
             *
             * AND WHEN IT IS DETACHED, `returnFocusTo` IS THE ANSWER rather than silence:
             * a Remove confirmation deletes the row its opener sits in by construction, so
             * "the element that had focus" is guaranteed to be gone exactly when the user
             * most needs their place back.
             */
            event.preventDefault();
            const opened = opener.current;
            const target = opened?.isConnected === true ? opened : (returnFocusTo?.current ?? null);
            if (target?.isConnected === true) {
              target.focus();
            }
          }}
        >
          {!attached && !alert && (
            <div
              className="flex shrink-0 justify-center py-2 md:hidden"
              // The grabber is decoration for the mouse and the drag surface for
              // the thumb; keyboard users close with Escape or Cancel.
              {...swipe.handlers}
            >
              <span className="h-1 w-10 rounded-pill bg-line-strong" />
            </div>
          )}

          {phoneBar && (
            <div data-dialog-actions="top" className="dialog-sheet-actions shrink-0 px-5 pt-3 pb-2">
              {actions}
            </div>
          )}
          <div className="flex shrink-0 items-start justify-between gap-3 px-5 pt-3 pb-2 md:pt-5">
            <div className="min-w-0">
              <DialogPrimitive.Title
                className={cx(
                  'text-md font-semibold tracking-tight text-ink',
                  hideTitle && 'sr-only',
                )}
              >
                {title}
              </DialogPrimitive.Title>
              {description !== undefined && (
                <DialogPrimitive.Description className="mt-1 text-sm text-ink-muted">
                  {descriptionOpenWord === undefined ? (
                    description
                  ) : (
                    <OpenWord id={descriptionOpenWord}>{description}</OpenWord>
                  )}
                </DialogPrimitive.Description>
              )}
            </div>
          </div>

          <div
            data-dialog-body
            className={cx(
              'min-h-0 flex-1 overflow-y-auto px-5 py-3',
              /**
               * THE HOME INDICATOR, on a sheet with NO FOOTER (found in the 2026-07-29
               * sheet audit, built 2026-07-30). The safe-area inset used to live on the
               * FOOTER alone, so a footer-less sheet rested its last row on the indicator -
               * which is every `MobileNav` sheet (Data, Setup, Account) and, until this
               * work package, would have been the Adjust sheet's sub-pages too.
               *
               * `max()` rather than a plain inset: on a phone without one, and on every
               * desktop, `env(safe-area-inset-bottom)` is 0 and the row would lose the
               * padding it already had. Skipped for the attached shape, which is not
               * attached to the bottom edge and therefore never reaches the indicator.
               */
              bottomRow === null &&
                !attached &&
                'pb-[max(var(--space-3),env(safe-area-inset-bottom))]',
            )}
          >
            {children}
          </div>

          {bottomRow !== null && (
            <div
              data-dialog-actions="bottom"
              className="shrink-0 border-t border-line px-5 py-3 pb-[max(var(--space-3),env(safe-area-inset-bottom))]"
            >
              {bottomRow}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>

      {/**
       * THE DIRTY CLOSE, asked on top of the sheet it would throw away (hig:
       * https://developer.apple.com/design/human-interface-guidelines/sheets - a sheet is
       * left through its own buttons, and the escape from a half-written form is a question
       * rather than a silent discard). It is `nested` so a press inside it is not a press
       * outside the sheet underneath, and it is rendered only while it is asked: a Dialog
       * that always rendered one would render one inside that one, without end.
       */}
      {asking && (
        <Dialog
          alert
          open
          onOpenChange={(next) => setAsking(next)}
          variant="center"
          width="sm"
          nested
          title="Discard your changes?"
          confirm={{
            label: 'Discard',
            tone: 'danger',
            onConfirm: () => {
              setAsking(false);
              onOpenChange(false);
            },
          }}
          cancelLabel="Keep editing"
        >
          <p className="text-sm text-ink-muted">
            What you typed here is not saved anywhere yet. Keep editing to go back to the sheet.
          </p>
        </Dialog>
      )}
    </DialogPrimitive.Root>
  );
}
